import type { AuthResponse, GameServerInfo, PlayerProfile } from "@mmotdg/shared";

export type AppScreen = "login" | "lobby" | "queue" | "match" | "room_browser" | "room";

export interface AppState {
  screen: AppScreen;
  token: string | null;
  refreshToken: string | null;
  user: AuthResponse["user"] | null;
  profile: PlayerProfile | null;
  selectedServer: GameServerInfo | null;
  currentRoomId: string | null;
  matchAssignment: {
    roomId: string;
    serverId: string;
    serverHost: string;
    serverPort: number;
    mode: "arena" | "coop" | "practice" | "boss_hunt" | "boss_endurance";
    tutorialLevel: number | null;
  } | null;
}

type Listener = (state: AppState) => void;

class Store {
  private state: AppState = {
    screen: "login",
    token: null,
    refreshToken: null,
    user: null,
    profile: null,
    selectedServer: null,
    currentRoomId: null,
    matchAssignment: null,
  };
  private listeners = new Set<Listener>();

  get(): AppState {
    return this.state;
  }

  set(partial: Partial<AppState>): void {
    this.state = { ...this.state, ...partial };
    for (const l of this.listeners) l(this.state);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }
}

export const appState = new Store();

const TOKEN_KEY = "mmotdg:token";
const REFRESH_KEY = "mmotdg:refresh";
const USER_KEY = "mmotdg:user";

export function loadSession(): void {
  const token = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  const userRaw = localStorage.getItem(USER_KEY);
  if (token && refreshToken && userRaw) {
    try {
      appState.set({
        token,
        refreshToken,
        user: JSON.parse(userRaw),
        screen: "lobby",
      });
    } catch {
      clearSession();
    }
  }
}

export function persistSession(token: string, refreshToken: string, user: AuthResponse["user"]): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  appState.set({
    token: null,
    refreshToken: null,
    user: null,
    profile: null,
    currentRoomId: null,
    matchAssignment: null,
    screen: "login",
  });
}
